// batch-67 hr-brieven (FO-506) — de PURE regels van de HR-besluitworkflows.
//
// Geen I/O, geen Prisma: alles hier is deterministisch en getest
// (`__tests__/hr-besluiten.test.ts`). De motor in `apps/api` voedt deze functies
// met rijen uit de database en schrijft de uitkomst terug.
//
//  - `berekenOpzegtermijn`      opzegtermijn uit de instelbare tabel (CAO / wet),
//                               uitkomst = vroegst mogelijke einddatum tegen het
//                               einde van de maand;
//  - `bepaalAanzegging`         aanzegtermijn (uiterlijk een maand voor het einde)
//                               met te-laat-signaal en vergoedingsplicht;
//  - `berekenBedenktermijn`     VSO: 14 dagen, 21 zonder vermelding;
//  - `berekenBezwaartermijn`    ontslag op staande voet: twee maanden;
//  - `bouwHrBesluitStappen`     het stappenplan per besluittype, met per stap de
//                               vereiste voorgangers (blokkade);
//  - `bepaalStapStatussen`      WACHT -> OPEN zodra de voorgangers klaar zijn;
//  - `magWorkflowOvergang`      de statusmachine van de workflow;
//  - `controleerHrBriefTags`    de tag-controle die bij de zeven HR-brieven
//                               blokkeert in plaats van waarschuwt;
//
// Datums met dag-precisie zijn 'yyyy-mm-dd' (NL-kalenderdag).

import {
  HR_BESLUIT_STAP_KLAAR_STATUSSEN,
  HR_BESLUIT_STAP_LABELS,
  HR_BRIEF_VARIANT_OPHEFFING,
  HR_BRIEF_VERPLICHTE_TAGS,
  HR_BRIEF_VERPLICHTE_TAGS_OPHEFFING,
  STAANDE_VOET_BEZWAARTERMIJN_MAANDEN,
  VSO_BEDENKTERMIJN_DAGEN,
  VSO_BEDENKTERMIJN_DAGEN_ZONDER_VERMELDING,
  AANZEGTERMIJN_MAANDEN,
  canonicalPlaceholderKey,
  isHrBriefCategorie,
  isPlaceholderSectieSleutel,
  type HrBesluitRol,
  type HrBesluitStapStatus,
  type HrBesluitStapType,
  type HrBesluitType,
  type HrBesluitWorkflowStatus,
  type OpzegtermijnBron,
  type OpzegtermijnPartij,
} from '@streep/types'
import { contractDagPlusDagen, contractDagPlusMaanden } from './contract-mijlpalen'
import { formatDagDatum, parseDagDatum } from './dagtaken-datum'
import { utcToAmsterdamWallclock } from './amsterdam-tijd'

// ─── Datumhulpen ─────────────────────────────────────────────────────────────

/** De laatste dag van de kalendermaand waarin `dag` valt. */
export function hrLaatsteDagVanMaand(dag: string): string {
  const d = parseDagDatum(dag)
  const laatste = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 0))
  return formatDagDatum(laatste)
}

/** De NL-kalenderdag ('yyyy-mm-dd') van een instant. */
export function hrNlDagVanInstant(instant: Date): string {
  return utcToAmsterdamWallclock(instant).ymd
}

/**
 * Een dag ('yyyy-mm-dd') zoals hij in zichtbare tekst hoort: dd-mm-jjjj
 * (toelichting, tijdlijn, bericht aan de salarisverwerker). Wat geen dag in
 * die vorm is komt ongewijzigd terug: nooit een halve of verzonnen datum.
 */
export function hrDagNl(dag: string | null | undefined): string {
  if (!dag) return ''
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dag)
  return m ? `${m[3]}-${m[2]}-${m[1]}` : dag
}

/** Kalenderdagen van `van` tot `tot` (positief als `tot` later ligt). */
export function hrDagenTussen(van: string, tot: string): number {
  const ms = parseDagDatum(tot).getTime() - parseDagDatum(van).getTime()
  return Math.round(ms / 86_400_000)
}

/** Hele dienstjaren op `peildatum` sinds `startDate` (null of in de toekomst = 0). */
export function dienstjarenOp(startDate: string | null, peildatum: string): number {
  if (!startDate) return 0
  const s = parseDagDatum(startDate)
  const p = parseDagDatum(peildatum)
  if (p.getTime() < s.getTime()) return 0
  let jaren = p.getUTCFullYear() - s.getUTCFullYear()
  const verjaardagGehaald =
    p.getUTCMonth() > s.getUTCMonth() ||
    (p.getUTCMonth() === s.getUTCMonth() && p.getUTCDate() >= s.getUTCDate())
  if (!verjaardagGehaald) jaren -= 1
  return Math.max(0, jaren)
}

// ─── Opzegtermijn ────────────────────────────────────────────────────────────

export interface OpzegtermijnRegel {
  bron: OpzegtermijnBron
  partij: OpzegtermijnPartij
  /** Ondergrens (inclusief). */
  minDienstjaren: number
  /** Bovengrens (exclusief); null = geen bovengrens. */
  maxDienstjaren: number | null
  termijnMaanden: number
  actief: boolean
  omschrijving?: string | null
}

export interface BerekenOpzegtermijnInput {
  partij: OpzegtermijnPartij
  /** Datum in dienst ('yyyy-mm-dd'), of null als onbekend. */
  startDate: string | null
  /** Dag waarop de opzegging is ontvangen. */
  ontvangenOp: string
  /** Gewenste einddatum van de medewerker; leeg = zo vroeg mogelijk. */
  gewensteEinddatum?: string | null
  tabel: readonly OpzegtermijnRegel[]
  /**
   * Welke bron voorgaat als beide een passende regel hebben. Default CAO: de CAO
   * Metaal en Techniek is op STREEP van toepassing; de wet is de terugval.
   */
  voorkeurBron?: OpzegtermijnBron
}

export interface BerekenOpzegtermijnResultaat {
  dienstjaren: number
  termijnMaanden: number
  bron: OpzegtermijnBron | null
  regelOmschrijving: string | null
  /** Vroegst mogelijke einddatum: tegen het einde van de maand. */
  vroegsteEinddatum: string
  /** De einddatum die geldt (gewenste als die later ligt, anders de vroegste). */
  einddatum: string
  /** True als geen regel paste en de wettelijke terugval van één maand is gebruikt. */
  terugval: boolean
}

/** Wettelijke terugval als de tabel geen passende regel heeft (art. 7:672 lid 3 BW: 1 maand). */
export const OPZEGTERMIJN_TERUGVAL_MAANDEN = 1

function regelPast(regel: OpzegtermijnRegel, partij: OpzegtermijnPartij, dienstjaren: number): boolean {
  if (!regel.actief || regel.partij !== partij) return false
  if (dienstjaren < regel.minDienstjaren) return false
  if (regel.maxDienstjaren !== null && dienstjaren >= regel.maxDienstjaren) return false
  return true
}

/**
 * Bereken de opzegtermijn en de vroegst mogelijke einddatum.
 *
 * Opzegging gebeurt tegen het einde van de maand: de termijn begint op de eerste
 * dag van de maand ná ontvangst en de einddatum is de laatste dag van de maand
 * waarin de termijn afloopt. Opzegging ontvangen op 14-10 met een termijn van
 * één maand geeft 30-11 (T7).
 */
export function berekenOpzegtermijn(input: BerekenOpzegtermijnInput): BerekenOpzegtermijnResultaat {
  const dienstjaren = dienstjarenOp(input.startDate, input.ontvangenOp)
  const voorkeur = input.voorkeurBron ?? 'CAO'
  const passend = input.tabel.filter((r) => regelPast(r, input.partij, dienstjaren))
  // Binnen een bron wint de meest specifieke regel (hoogste ondergrens).
  const kies = (bron: OpzegtermijnBron): OpzegtermijnRegel | null =>
    passend
      .filter((r) => r.bron === bron)
      .sort((a, b) => b.minDienstjaren - a.minDienstjaren)[0] ?? null
  const regel = kies(voorkeur) ?? kies(voorkeur === 'CAO' ? 'WETTELIJK' : 'CAO')
  const termijnMaanden = regel ? regel.termijnMaanden : OPZEGTERMIJN_TERUGVAL_MAANDEN

  const vroegsteEinddatum = hrLaatsteDagVanMaand(contractDagPlusMaanden(input.ontvangenOp, termijnMaanden))
  // Opzeggen gebeurt tegen het einde van de maand (art. 7:672 lid 1 BW). Wil de
  // medewerker later weg dan het vroegst mogelijke einde, dan is de einddatum
  // het einde van de maand waarin die gewenste dag valt; eerder dan het
  // vroegst mogelijke einde kan niet. De uitkomst is dus altijd een maandeinde.
  const gewenst = input.gewensteEinddatum ?? null
  const einddatum = gewenst && gewenst > vroegsteEinddatum ? hrLaatsteDagVanMaand(gewenst) : vroegsteEinddatum

  return {
    dienstjaren,
    termijnMaanden,
    bron: regel ? regel.bron : null,
    regelOmschrijving: regel?.omschrijving ?? null,
    vroegsteEinddatum,
    einddatum,
    terugval: regel === null,
  }
}

// ─── Aanzegging ──────────────────────────────────────────────────────────────

export interface AanzeggingResultaat {
  /** Uiterste dag voor de aanzegging: een maand voor het contracteinde. */
  uiterlijkOp: string
  teLaat: boolean
  /** Kalenderdagen te laat (0 als op tijd). */
  dagenTeLaat: number
  /**
   * Te laat aanzeggen geeft recht op een vergoeding naar rato (max. een
   * maandsalaris, art. 7:668 lid 3 BW). Het bedrag zelf wordt hier bewust niet
   * berekend (geen salaris in deze laag): alleen de plicht en de omvang in dagen.
   */
  vergoedingsplicht: boolean
  /** True als de volledige maand verschuldigd is (30 dagen of meer te laat, of na het einde). */
  volledigeMaand: boolean
}

export function bepaalAanzegging(input: { contractEindDatum: string; aanzegdatum: string }): AanzeggingResultaat {
  const uiterlijkOp = contractDagPlusMaanden(input.contractEindDatum, -AANZEGTERMIJN_MAANDEN)
  const dagenTeLaat = Math.max(0, hrDagenTussen(uiterlijkOp, input.aanzegdatum))
  const teLaat = dagenTeLaat > 0
  return {
    uiterlijkOp,
    teLaat,
    dagenTeLaat,
    vergoedingsplicht: teLaat,
    volledigeMaand: teLaat && (dagenTeLaat >= 30 || input.aanzegdatum >= input.contractEindDatum),
  }
}

// ─── Bedenktermijn en bezwaartermijn ─────────────────────────────────────────

export interface BedenktermijnResultaat {
  dagen: number
  /** Laatste dag waarop herroepen kan (inclusief), na verlenging volgens de Algemene termijnenwet. */
  tot: string
  /** De dag zonder die verlenging (ondertekening plus de dagen). */
  nominaalTot: string
  /** True als de termijn op een weekend- of feestdag eindigde en daarom doorloopt. */
  verlengd: boolean
}

/** Art. 7:670b BW: 14 dagen na ondertekening, 21 als de VSO het recht niet vermeldt. */
/** Paaszondag van een jaar (Gregoriaanse kalender, het algoritme van Meeus/Jones/Butcher). */
function paaszondag(jaar: number): string {
  const a = jaar % 19
  const b = Math.floor(jaar / 100)
  const c = jaar % 100
  const d = Math.floor(b / 4)
  const e = b % 4
  const f = Math.floor((b + 8) / 25)
  const g = Math.floor((b - f + 1) / 3)
  const h = (19 * a + b - d - g + 15) % 30
  const i = Math.floor(c / 4)
  const k = c % 4
  const l = (32 + 2 * e + 2 * i - h - k) % 7
  const m = Math.floor((a + 11 * h + 22 * l) / 451)
  const maand = Math.floor((h + l - 7 * m + 114) / 31)
  const dag = ((h + l - 7 * m + 114) % 31) + 1
  return `${jaar}-${String(maand).padStart(2, '0')}-${String(dag).padStart(2, '0')}`
}

function weekdagVan(ymd: string): number {
  const [j, m, d] = ymd.split('-').map(Number)
  return new Date(Date.UTC(j ?? 1970, (m ?? 1) - 1, d ?? 1)).getUTCDay()
}

/**
 * De algemeen erkende feestdagen van de Algemene termijnenwet (art. 3) voor een
 * jaar: Nieuwjaarsdag, Goede Vrijdag (gelijkgesteld), tweede Paasdag,
 * Koningsdag (de dag waarop hij gevierd wordt), 5 mei, Hemelvaartsdag, tweede
 * Pinksterdag en beide Kerstdagen. Dit is de wettelijke lijst voor termijnen,
 * los van de feestdagen in het bedrijfsrooster.
 */
export function atwFeestdagen(jaar: number): string[] {
  const pasen = paaszondag(jaar)
  const koningsdag = weekdagVan(`${jaar}-04-27`) === 0 ? `${jaar}-04-26` : `${jaar}-04-27`
  return [
    `${jaar}-01-01`,
    contractDagPlusDagen(pasen, -2),
    contractDagPlusDagen(pasen, 1),
    koningsdag,
    `${jaar}-05-05`,
    contractDagPlusDagen(pasen, 39),
    contractDagPlusDagen(pasen, 50),
    `${jaar}-12-25`,
    `${jaar}-12-26`,
  ]
}

/**
 * Algemene termijnenwet art. 1: een wettelijke termijn die op een zaterdag,
 * zondag of algemeen erkende feestdag eindigt, loopt door tot en met de
 * eerstvolgende dag die dat niet is. Geldt voor termijnen tot drie maanden,
 * dus voor de bedenktermijn (art. 7:670b BW) en de vervaltermijn na een ontslag
 * op staande voet (art. 7:686a lid 4 BW).
 */
export function verlengTermijnTotWerkdag(ymd: string): string {
  let dag = ymd
  for (let i = 0; i < 10; i += 1) {
    const weekdag = weekdagVan(dag)
    const feestdag = atwFeestdagen(Number(dag.slice(0, 4))).includes(dag)
    if (weekdag !== 0 && weekdag !== 6 && !feestdag) return dag
    dag = contractDagPlusDagen(dag, 1)
  }
  return dag
}

/**
 * De bedenktermijn van een VSO: 14 dagen na ondertekening, 21 als de
 * overeenkomst het recht niet vermeldt. `tot` is de laatste dag waarop de
 * medewerker kan herroepen, verlengd volgens de Algemene termijnenwet als de
 * termijn op een weekend- of feestdag eindigt (`nominaalTot` is de dag zonder
 * die verlenging).
 */
export function berekenBedenktermijn(input: { ondertekendOp: string; vermeld: boolean }): BedenktermijnResultaat {
  const dagen = input.vermeld ? VSO_BEDENKTERMIJN_DAGEN : VSO_BEDENKTERMIJN_DAGEN_ZONDER_VERMELDING
  const nominaalTot = contractDagPlusDagen(input.ondertekendOp, dagen)
  const tot = verlengTermijnTotWerkdag(nominaalTot)
  return { dagen, tot, nominaalTot, verlengd: tot !== nominaalTot }
}
export function isBinnenBedenktermijn(herroepenOp: string, tot: string): boolean {
  return herroepenOp <= tot
}

/** Art. 7:686a lid 4 BW: de medewerker kan twee maanden na het ontslag naar de rechter. */
export function berekenBezwaartermijn(ontslagMoment: Date): string {
  return verlengTermijnTotWerkdag(contractDagPlusMaanden(hrNlDagVanInstant(ontslagMoment), STAANDE_VOET_BEZWAARTERMIJN_MAANDEN))
}

// ─── Controle-rol (4-ogen, aanname A3) ───────────────────────────────────────

/** Directie bij een VSO en bij ontslag op staande voet; een tweede HR-medewerker bij de rest. */
export function bepaalControleRol(type: HrBesluitType): HrBesluitRol {
  return type === 'VSO' || type === 'ONTSLAG_STAANDE_VOET' ? 'DIRECTIE' : 'HR'
}

// ─── Stappenplan ─────────────────────────────────────────────────────────────

export interface HrBesluitStapPlan {
  type: HrBesluitStapType
  titel: string
  verantwoordelijkeRol: HrBesluitRol
  vereisteStappen: HrBesluitStapType[]
  /** 'yyyy-mm-dd' of null. */
  deadlineOp: string | null
  /** Beginstatus: automatische checks die bij de start al zijn uitgevoerd staan direct op AFGEROND. */
  beginStatus: HrBesluitStapStatus
}

export interface BouwStappenOpties {
  /** Verlenging/omzetting: wijzigen de arbeidsvoorwaarden (salarisverwerker-bericht)? */
  voorwaardenGewijzigd?: boolean
  /** Non-actief: middelen innemen (optionele taak). */
  innameMiddelen?: boolean
  /** Acceptatie: opzegging zonder schriftelijke bron (WhatsApp, mondeling). */
  bronZonderSchrift?: boolean
  /** VSO: vrijstelling van werk afgesproken. */
  vrijstellingVanWerk?: boolean
  /** Deadline van de aanzegging (opzegging bepaalde tijd). */
  aanzegUiterlijkOp?: string | null
  /** Non-actief: de einddatum (herbeoordeling). */
  herbeoordelingOp?: string | null
  /** Staande voet: einde van de bezwaartermijn. */
  bezwaartermijnTot?: string | null
  /** Acceptatie/VSO/opzegging: de einddatum van het dienstverband (deadline voor de uitdienst-stappen). */
  einddatum?: string | null
}

const KLAAR: HrBesluitStapStatus = 'AFGEROND'
const WACHT: HrBesluitStapStatus = 'WACHT'

function stap(
  type: HrBesluitStapType,
  rol: HrBesluitRol,
  vereist: HrBesluitStapType[],
  extra: { deadlineOp?: string | null; beginStatus?: HrBesluitStapStatus; titel?: string } = {},
): HrBesluitStapPlan {
  return {
    type,
    titel: extra.titel ?? HR_BESLUIT_STAP_LABELS[type],
    verantwoordelijkeRol: rol,
    vereisteStappen: vereist,
    deadlineOp: extra.deadlineOp ?? null,
    beginStatus: extra.beginStatus ?? WACHT,
  }
}

/** De brief-keten die elk besluit deelt: concept -> controle -> definitief -> verstrekken. */
function briefKeten(controleRol: HrBesluitRol, verstrekDeadline: string | null = null): HrBesluitStapPlan[] {
  return [
    stap('BRIEF_CONCEPT', 'HR', []),
    stap('CONTROLE_4_OGEN', controleRol, ['BRIEF_CONCEPT']),
    stap('BRIEF_DEFINITIEF', 'HR', ['CONTROLE_4_OGEN']),
    stap('BRIEF_VERSTREKKEN', 'HR', ['BRIEF_DEFINITIEF'], { deadlineOp: verstrekDeadline }),
  ]
}

/** De uitdienst-keten: inname vóór eindafrekening (spec §B8: "geen eindafrekening vóór inname"). */
function uitdienstKeten(na: HrBesluitStapType, einddatum: string | null): HrBesluitStapPlan[] {
  return [
    stap('MIDDELEN_INNEMEN', 'FACILITIES', [na], { deadlineOp: einddatum }),
    stap('GETUIGSCHRIFT', 'HR', [na], { deadlineOp: einddatum }),
    // Ook de start zelf: wordt de inname overgeslagen, dan gaat de
    // eindafrekening nog steeds niet open voordat de offboarding er is.
    stap('EINDAFREKENING', 'SALARIS', ['MIDDELEN_INNEMEN', na], { deadlineOp: einddatum }),
  ]
}

/**
 * Het stappenplan per besluittype. De volgorde van de lijst is de volgorde in de
 * tijdlijn; `vereisteStappen` bepaalt de blokkade (een stap gaat pas OPEN als
 * al zijn voorgangers AFGEROND of OVERGESLAGEN zijn).
 */
export function bouwHrBesluitStappen(type: HrBesluitType, opties: BouwStappenOpties = {}): HrBesluitStapPlan[] {
  const controleRol = bepaalControleRol(type)
  switch (type) {
    case 'VERLENGING_BEPAALDE_TIJD':
    case 'OMZETTING_ONBEPAALDE_TIJD': {
      const stappen: HrBesluitStapPlan[] = []
      if (type === 'VERLENGING_BEPAALDE_TIJD') {
        stappen.push(stap('KETENCHECK', 'HR', [], { beginStatus: KLAAR }))
      }
      stappen.push(...briefKeten(controleRol))
      stappen.push(
        stap('CONTRACT_AANMAKEN', 'HR', ['CONTROLE_4_OGEN']),
        stap('CONTRACT_ONDERTEKENEN', 'HR', ['CONTRACT_AANMAKEN']),
        stap('CONTRACT_ACTIVEREN', 'HR', ['CONTRACT_ONDERTEKENEN']),
        stap('CONTRACTMUTATIE', 'HR', ['CONTRACT_ONDERTEKENEN']),
        // Pas na de handtekening: voor die tijd is er nog niets overeengekomen
        // om aan de salarisverwerker door te geven (spec §B2: het bericht
        // volgt op ondertekenen en de contractmutatie).
        stap('SALARISVERWERKER_BERICHT', 'SALARIS', ['CONTRACT_ONDERTEKENEN'], {
          beginStatus: opties.voorwaardenGewijzigd ? WACHT : 'OVERGESLAGEN',
        }),
      )
      return stappen
    }
    case 'OPZEGGING_BEPAALDE_TIJD':
      return [
        stap('AANZEGCHECK', 'HR', [], { beginStatus: KLAAR }),
        ...briefKeten(controleRol, opties.aanzegUiterlijkOp ?? null),
        stap('OFFBOARDING_STARTEN', 'HR', ['BRIEF_VERSTREKKEN']),
        ...uitdienstKeten('OFFBOARDING_STARTEN', opties.einddatum ?? null),
      ]
    case 'NON_ACTIEFSTELLING':
      return [
        stap('VERZUIMCHECK', 'HR', [], { beginStatus: KLAAR }),
        ...briefKeten(controleRol),
        stap('WERKSTATUS_NON_ACTIEF', 'HR', ['BRIEF_VERSTREKKEN']),
        stap('VERLOF_AFBOEKEN', 'HR', ['WERKSTATUS_NON_ACTIEF']),
        stap('MIDDELEN_INNEMEN', 'FACILITIES', ['WERKSTATUS_NON_ACTIEF'], {
          beginStatus: opties.innameMiddelen ? WACHT : 'OVERGESLAGEN',
        }),
        stap('HERBEOORDELING', 'HR', ['WERKSTATUS_NON_ACTIEF'], { deadlineOp: opties.herbeoordelingOp ?? null }),
        stap('OPHEFFING', 'HR', ['HERBEOORDELING']),
      ]
    case 'VSO':
      return [
        ...briefKeten(controleRol),
        stap('ONDERTEKENING', 'HR', ['BRIEF_VERSTREKKEN']),
        stap('BEDENKTERMIJN', 'HR', ['ONDERTEKENING']),
        stap('OFFBOARDING_STARTEN', 'HR', ['BEDENKTERMIJN']),
        stap('VRIJSTELLING_WERK', 'HR', ['BEDENKTERMIJN'], {
          beginStatus: opties.vrijstellingVanWerk ? WACHT : 'OVERGESLAGEN',
        }),
        ...uitdienstKeten('OFFBOARDING_STARTEN', opties.einddatum ?? null),
        stap('SALARISVERWERKER_BERICHT', 'SALARIS', ['BEDENKTERMIJN']),
      ]
    case 'ONTSLAG_STAANDE_VOET':
      return [
        stap('FEITEN_VASTGELEGD', 'HR', [], { beginStatus: KLAAR }),
        stap('BRIEF_CONCEPT', 'HR', []),
        stap('CONTROLE_4_OGEN', controleRol, ['BRIEF_CONCEPT']),
        stap('BRIEF_DEFINITIEF', 'HR', ['CONTROLE_4_OGEN']),
        stap('VERZENDING_ONVERWIJLD', 'HR', ['BRIEF_DEFINITIEF']),
        stap('TOEGANG_BLOKKEREN', 'HR', ['VERZENDING_ONVERWIJLD']),
        stap('PLANNING_LEEGMAKEN', 'PLANNER', ['VERZENDING_ONVERWIJLD']),
        stap('OFFBOARDING_STARTEN', 'HR', ['VERZENDING_ONVERWIJLD']),
        // De inname loopt via de inleverlijst van de offboarding (met spoed):
        // daarom pas na de start daarvan, en nooit een tweede taak ernaast.
        stap('MIDDELEN_INNEMEN', 'FACILITIES', ['OFFBOARDING_STARTEN'], { titel: 'Middelen innemen (spoed)' }),
        stap('EINDAFREKENING', 'SALARIS', ['MIDDELEN_INNEMEN', 'OFFBOARDING_STARTEN']),
        stap('BEZWAARTERMIJN', 'HR', ['VERZENDING_ONVERWIJLD'], { deadlineOp: opties.bezwaartermijnTot ?? null }),
      ]
    case 'ACCEPTATIE_OPZEGGING':
      return [
        stap('OPZEGGING_GEREGISTREERD', 'HR', [], { beginStatus: KLAAR }),
        stap('SCHRIFTELIJKE_BEVESTIGING', 'HR', [], {
          beginStatus: opties.bronZonderSchrift ? WACHT : 'OVERGESLAGEN',
        }),
        stap('OPZEGTERMIJN_BEREKEND', 'HR', [], { beginStatus: KLAAR }),
        ...briefKeten(controleRol),
        stap('VERLOFKEUZE', 'HR', []),
        stap('OFFBOARDING_STARTEN', 'HR', ['BRIEF_VERSTREKKEN']),
        ...uitdienstKeten('OFFBOARDING_STARTEN', opties.einddatum ?? null),
        stap('SALARISVERWERKER_BERICHT', 'SALARIS', ['OFFBOARDING_STARTEN', 'VERLOFKEUZE']),
      ]
    default: {
      const _exhaustive: never = type
      return _exhaustive
    }
  }
}

// ─── Soorten stappen (een bron voor de motor en het scherm) ─────────────────

/**
 * Stappen die het systeem zelf uitvoert zodra ze open gaan (geen taak, geen
 * melding): de motor voert ze uit en rondt ze af. Mislukt dat, dan blijft de
 * stap open met de fout in de toelichting en rondt HR hem handmatig af.
 */
export const HR_BESLUIT_AUTOMATISCHE_STAPPEN: ReadonlySet<HrBesluitStapType> = new Set<HrBesluitStapType>([
  'OFFBOARDING_STARTEN',
  'WERKSTATUS_NON_ACTIEF',
  'VERLOF_AFBOEKEN',
  'TOEGANG_BLOKKEREN',
  'VRIJSTELLING_WERK',
])

/**
 * Stappen die op een gebeurtenis wachten (een handtekening, een verstreken
 * termijn): geen taak. De handtekening-hook en de dagelijkse termijnbewaking
 * ronden ze af.
 */
export const HR_BESLUIT_WACHT_STAPPEN: ReadonlySet<HrBesluitStapType> = new Set<HrBesluitStapType>([
  'CONTRACT_ONDERTEKENEN',
  'ONDERTEKENING',
  'BEDENKTERMIJN',
  'BEZWAARTERMIJN',
])

/**
 * Stappen met een eigen handeling in het scherm (een formulier, een brief, een
 * bericht): die rond je niet af met de losse knop "afronden".
 */
export const HR_BESLUIT_EIGEN_ACTIE_STAPPEN: ReadonlySet<HrBesluitStapType> = new Set<HrBesluitStapType>([
  'BRIEF_CONCEPT',
  'CONTROLE_4_OGEN',
  'BRIEF_DEFINITIEF',
  'BRIEF_VERSTREKKEN',
  'VERZENDING_ONVERWIJLD',
  'CONTRACT_AANMAKEN',
  'HERBEOORDELING',
  'OPHEFFING',
  'VERLOFKEUZE',
  'SALARISVERWERKER_BERICHT',
])

/**
 * Stappen die de offboarding-cockpit volgen zodra het besluit een offboarding
 * heeft gestart: de inleverlijst, het getuigschrift en de eindafrekening leven
 * daar (met hun eigen taken). Het besluit spiegelt alleen de stand, zodat er
 * nooit twee taken voor hetzelfde werk ontstaan.
 */
export const HR_BESLUIT_SPIEGEL_STAPPEN: ReadonlySet<HrBesluitStapType> = new Set<HrBesluitStapType>([
  'MIDDELEN_INNEMEN',
  'GETUIGSCHRIFT',
  'EINDAFREKENING',
])

/**
 * Mag HR deze stap overslaan (met reden)? Alleen het handwerk dat in de
 * praktijk ook buiten STREEP om kan gebeuren. De briefketen, de controle, de
 * verzending, alles wat het systeem zelf doet, alles wat op een gebeurtenis
 * wacht en de herbeoordeling en opheffing van een non-actiefstelling zijn
 * nooit over te slaan: daar hangt de blokkade van de vervolgstappen aan.
 */
export function isStapOverslaanbaar(type: HrBesluitStapType): boolean {
  if (HR_BESLUIT_AUTOMATISCHE_STAPPEN.has(type) || HR_BESLUIT_WACHT_STAPPEN.has(type)) return false
  const nooit: readonly HrBesluitStapType[] = [
    'BRIEF_CONCEPT',
    'CONTROLE_4_OGEN',
    'BRIEF_DEFINITIEF',
    'BRIEF_VERSTREKKEN',
    'VERZENDING_ONVERWIJLD',
    'HERBEOORDELING',
    'OPHEFFING',
  ]
  return !nooit.includes(type)
}

/**
 * Sluit een in de takenmodule afgevinkte taak de stap? Ja voor gewoon handwerk
 * en voor het bericht aan de salarisverwerker (wie de taak afvinkt verklaart
 * dat de verwerker op de hoogte is); nee voor stappen die een handeling in het
 * besluit zelf vragen (brief, controle, herbeoordeling).
 */
export function sluitTaakDeStap(type: HrBesluitStapType): boolean {
  if (HR_BESLUIT_AUTOMATISCHE_STAPPEN.has(type) || HR_BESLUIT_WACHT_STAPPEN.has(type)) return false
  if (type === 'SALARISVERWERKER_BERICHT') return true
  return !HR_BESLUIT_EIGEN_ACTIE_STAPPEN.has(type)
}

// ─── Stapstatussen en blokkade ───────────────────────────────────────────────

export interface StapStand {
  type: HrBesluitStapType
  status: HrBesluitStapStatus
  vereisteStappen: readonly HrBesluitStapType[]
}

export function isStapKlaar(status: HrBesluitStapStatus): boolean {
  return (HR_BESLUIT_STAP_KLAAR_STATUSSEN as readonly HrBesluitStapStatus[]).includes(status) || status === 'VERVALLEN'
}

/**
 * Herleid per stap of hij OPEN of WACHT is. Afgeronde, overgeslagen en vervallen
 * stappen blijven zoals ze zijn. Een vereiste stap die niet in de lijst
 * voorkomt telt als klaar (de regel kan niet op iets wachten dat niet bestaat).
 */
export function bepaalStapStatussen<T extends StapStand>(stappen: readonly T[]): Array<T & { status: HrBesluitStapStatus }> {
  const perType = new Map<HrBesluitStapType, HrBesluitStapStatus>()
  for (const s of stappen) perType.set(s.type, s.status)
  return stappen.map((s) => {
    if (isStapKlaar(s.status)) return s
    const geblokkeerd = s.vereisteStappen.some((v) => {
      const status = perType.get(v)
      return status !== undefined && !isStapKlaar(status)
    })
    return { ...s, status: geblokkeerd ? 'WACHT' : 'OPEN' }
  })
}

/** De voorgangers van een stap die nog niet klaar zijn (voor de uitleg in de UI). */
export function ontbrekendeVoorgangers(stapType: HrBesluitStapType, stappen: readonly StapStand[]): HrBesluitStapType[] {
  const eigen = stappen.find((s) => s.type === stapType)
  if (!eigen) return []
  return eigen.vereisteStappen.filter((v) => {
    const voorganger = stappen.find((s) => s.type === v)
    return voorganger !== undefined && !isStapKlaar(voorganger.status)
  })
}

/** Alle stappen klaar? Dan kan de workflow op AFGEROND. */
export function magWorkflowAfronden(stappen: readonly StapStand[]): boolean {
  return stappen.every((s) => isStapKlaar(s.status))
}

// ─── Statusmachine van de workflow ───────────────────────────────────────────

export const HR_BESLUIT_WORKFLOW_OVERGANGEN: Record<HrBesluitWorkflowStatus, readonly HrBesluitWorkflowStatus[]> = {
  CONCEPT: ['TER_CONTROLE', 'GEANNULEERD'],
  TER_CONTROLE: ['LOPEND', 'CONCEPT', 'GEANNULEERD'],
  LOPEND: ['AFGEROND', 'HERROEPEN', 'GEANNULEERD'],
  AFGEROND: [],
  HERROEPEN: [],
  GEANNULEERD: [],
}

export function magWorkflowOvergang(van: HrBesluitWorkflowStatus, naar: HrBesluitWorkflowStatus): boolean {
  return HR_BESLUIT_WORKFLOW_OVERGANGEN[van].includes(naar)
}

/** Herroepen kan alleen bij een VSO en alleen zolang de bedenktermijn loopt. */
export function magHerroepen(input: {
  type: HrBesluitType
  status: HrBesluitWorkflowStatus
  bedenktermijnTot: string | null
  herroepenOp: string
  /** De NL-dag van ondertekening; een herroeping kan daar niet voor liggen. */
  ondertekendOp?: string | null
  /** De NL-dag van vandaag; een herroeping kan niet in de toekomst liggen. */
  vandaag?: string | null
}): { ok: true } | { ok: false; reden: string } {
  if (input.type !== 'VSO') return { ok: false, reden: 'Alleen een vaststellingsovereenkomst kan herroepen worden.' }
  if (input.status !== 'LOPEND') return { ok: false, reden: 'De workflow loopt niet (meer).' }
  if (!input.bedenktermijnTot) return { ok: false, reden: 'De bedenktermijn is nog niet gestart: de VSO is nog niet ondertekend.' }
  if (input.vandaag && input.herroepenOp > input.vandaag) {
    return { ok: false, reden: 'De datum van de herroeping ligt in de toekomst.' }
  }
  if (input.ondertekendOp && input.herroepenOp < input.ondertekendOp) {
    return { ok: false, reden: `De herroeping kan niet voor de ondertekening (${hrDagNl(input.ondertekendOp)}) liggen.` }
  }
  if (!isBinnenBedenktermijn(input.herroepenOp, input.bedenktermijnTot)) {
    return { ok: false, reden: `De bedenktermijn is verstreken op ${hrDagNl(input.bedenktermijnTot)}.` }
  }
  return { ok: true }
}

// ─── Tag-controle (blokkade bij de HR-brieven) ───────────────────────────────

export interface ControleerHrBriefTagsInput {
  category: string | null | undefined
  variant?: string | null
  /** De keys uit de scan van het sjabloon (ruwe schrijfwijze mag). */
  scanKeys: readonly string[]
  /** Tags die bij de render geen waarde kregen (genormaliseerd of ruw). */
  ontbrekendeTags: readonly string[]
}

export interface ControleerHrBriefTagsResultaat {
  /** False voor niet-HR-categorieën: dan geldt de gewone waarschuwing. */
  vanToepassing: boolean
  ok: boolean
  /** Verplichte tags (canoniek) die niet in het sjabloon staan. */
  ontbrekendeVerplichteTags: string[]
  /** Tags in het sjabloon die bij de render geen waarde kregen. */
  tagsZonderWaarde: string[]
}

/** De verplichte tags voor een sjabloon (hoofdbrief of de opheffingsvariant). */
export function verplichteHrBriefTags(category: string | null | undefined, variant?: string | null): readonly string[] {
  if (!isHrBriefCategorie(category)) return []
  if (category === 'NON_ACTIEFSTELLING' && variant === HR_BRIEF_VARIANT_OPHEFFING) {
    return HR_BRIEF_VERPLICHTE_TAGS_OPHEFFING
  }
  return HR_BRIEF_VERPLICHTE_TAGS[category]
}

/**
 * FO-506 T8 / correctie 3: bij de zeven HR-brieven is een sjabloon zonder de
 * verplichte tags of met een tag die leeg rendert GEEN waarschuwing maar een
 * blokkade. Andere categorieën houden het FO-453-gedrag (waarschuwen, nooit
 * blokkeren).
 */
export function controleerHrBriefTags(input: ControleerHrBriefTagsInput): ControleerHrBriefTagsResultaat {
  if (!isHrBriefCategorie(input.category)) {
    return { vanToepassing: false, ok: true, ontbrekendeVerplichteTags: [], tagsZonderWaarde: [] }
  }
  const aanwezig = new Set(
    input.scanKeys.filter((k) => !isPlaceholderSectieSleutel(k)).map((k) => canonicalPlaceholderKey(k)),
  )
  const verplicht = verplichteHrBriefTags(input.category, input.variant)
  const ontbrekendeVerplichteTags = verplicht.filter((t) => !aanwezig.has(canonicalPlaceholderKey(t)))
  const tagsZonderWaarde = Array.from(
    new Set(
      input.ontbrekendeTags
        .map((k) => k.trim())
        .filter((k) => k !== '' && k !== '.' && !isPlaceholderSectieSleutel(k) && canonicalPlaceholderKey(k) !== 'handtekening'),
    ),
  ).sort((a, b) => a.localeCompare(b, 'nl'))
  return {
    vanToepassing: true,
    ok: ontbrekendeVerplichteTags.length === 0 && tagsZonderWaarde.length === 0,
    ontbrekendeVerplichteTags,
    tagsZonderWaarde,
  }
}
